/**
 * manager-cart-drawer.js
 * Gerencia o drawer lateral do carrinho.
 *
 * Dependências (devem carregar antes):
 *   - window.eventBus       (manager-global-event-bus.js)
 *   - window.DrawerManager  (manager-drawer.js)
 *   - window.CartManager    (manager-cart.js)
 *
 * ─── Configuração em Liquid (dentro da section cart-drawer.liquid) ─────────
 *
 *   <script>
 *     window.cartDrawerConfig = { sectionId: '{{ section.id }}' };
 *   </script>
 *
 * ─── Data attributes ──────────────────────────────────────────────────────
 *
 *   data-cart-drawer-content   → wrapper do conteúdo substituído no refresh
 *   data-cart-drawer-loading   → indicador de loading (hidden por padrão)
 *
 *   data-cart-item             → wrapper de um item do carrinho
 *   data-remove-item           → botão remover (lê data-line-item-key)
 *   data-quantity-minus        → botão decrementar (lê data-line-item-key)
 *   data-quantity-plus         → botão incrementar (lê data-line-item-key)
 *   data-quantity-value        → span com quantidade atual
 *   data-line-item-key         → chave do line item no carrinho
 *
 *   data-cart-count            → exibe contagem total de itens
 *   data-cart-count-zero       → hidden quando carrinho vazio
 *
 * ─── API pública ──────────────────────────────────────────────────────────
 *
 *   await window.CartDrawerManager.refresh()
 *   window.CartDrawerManager.destroy()
 */

class CartDrawerManager {
  constructor() {
    this._sectionId   = window.cartDrawerConfig?.sectionId;
    this._isRefreshing = false;

    this._onCartAdded   = this._onCartAdded.bind(this);
    this._onCartUpdated = this._onCartUpdated.bind(this);
    this._onCartRemoved = this._onCartRemoved.bind(this);
    this._onContentClick = this._onContentClick.bind(this);

    this._bindEvents();
  }

  // ─── Bind de eventos ──────────────────────────────────────────────────────

  _bindEvents() {
    document.addEventListener('cart:added',   this._onCartAdded);
    document.addEventListener('cart:updated', this._onCartUpdated);
    document.addEventListener('cart:removed', this._onCartRemoved);
    document.addEventListener('click',        this._onContentClick);
  }

  // ─── Resposta a eventos do CartManager ───────────────────────────────────

  async _onCartAdded() {
    await this.refresh();
  }

  async _onCartUpdated() {
    await this.refresh();
  }

  async _onCartRemoved() {
    await this.refresh();
  }

  // ─── Event delegation para ações dentro do drawer ────────────────────────

  _onContentClick(e) {
    // Remover item
    const removeBtn = e.target.closest('[data-remove-item]');
    if (removeBtn) {
      const key = removeBtn.dataset.lineItemKey;
      if (key) window.CartManager?.remove(key, { button: removeBtn });
      return;
    }

    // Decrementar quantidade
    const minusBtn = e.target.closest('[data-quantity-minus]');
    if (minusBtn) {
      const key = minusBtn.dataset.lineItemKey;
      const qty = this._getQuantity(minusBtn);
      const item = minusBtn.closest('[data-cart-item]');
      const errorEl = item?.querySelector('[data-inventory-error]');
      if (errorEl) errorEl.setAttribute('hidden', '');

      if (!key) return;
      if (qty <= 1) {
        window.CartManager?.remove(key, { button: minusBtn });
      } else {
        window.CartManager?.update(key, qty - 1, { button: minusBtn });
      }
      return;
    }

    // Incrementar quantidade
    const plusBtn = e.target.closest('[data-quantity-plus]');
    if (plusBtn) {
      const key = plusBtn.dataset.lineItemKey;
      const qty = this._getQuantity(plusBtn);
      const item = plusBtn.closest('[data-cart-item]');
      
      const management = item?.dataset.inventoryManagement;
      const policy = item?.dataset.inventoryPolicy;
      const inventoryQty = parseInt(item?.dataset.inventoryQuantity || '0', 10);
      const errorEl = item?.querySelector('[data-inventory-error]');
      
      if (management === 'shopify' && policy === 'deny' && qty >= inventoryQty) {
        if (errorEl) errorEl.removeAttribute('hidden');
        return;
      }
      
      if (errorEl) errorEl.setAttribute('hidden', '');
      
      if (key) window.CartManager?.update(key, qty + 1, { button: plusBtn });
      return;
    }
  }

  /** Lê a quantidade atual de um item a partir do botão de controle. */
  _getQuantity(btn) {
    const item = btn.closest('[data-cart-item]');
    const valueEl = item?.querySelector('span[data-quantity-value]');
    return parseInt(valueEl?.textContent?.trim() || '1', 10);
  }

  // ─── Section Rendering API ────────────────────────────────────────────────

  /**
   * Atualiza o conteúdo do drawer via Section Rendering API.
   * Previne chamadas concorrentes com a flag _isRefreshing.
   */
  async refresh() {
    if (this._isRefreshing || !this._sectionId) return;
    this._isRefreshing = true;
    this._setLoading(true);

    try {
      const res = await fetch(`/?sections=${this._sectionId}`);
      if (!res.ok) throw new Error(`HTTP ${res.status}`);

      const sections = await res.json();
      const html     = sections[this._sectionId];
      if (!html) return;

      const doc        = new DOMParser().parseFromString(html, 'text/html');
      const newContent = doc.querySelector('[data-cart-drawer-content]');
      const curContent = document.querySelector('[data-cart-drawer-content]');

      if (newContent && curContent) {
        curContent.innerHTML = newContent.innerHTML;
      }

      this._updateCount(doc);
    } catch (err) {
      console.error('[CartDrawerManager] Refresh failed:', err);
    } finally {
      this._isRefreshing = false;
      this._setLoading(false);
    }
  }

  /** Mostra ou oculta o indicador de loading. */
  _setLoading(show) {
    const el = document.querySelector('[data-cart-drawer-loading]');
    if (!el) return;
    show ? el.removeAttribute('hidden') : el.setAttribute('hidden', '');
  }

  /** Atualiza todos os contadores de itens do carrinho na página. */
  _updateCount(doc) {
    const countEl = doc.querySelector('[data-cart-count]');
    if (!countEl) return;
    const count = countEl.textContent.trim();

    document.querySelectorAll('[data-cart-count]').forEach(el => {
      el.textContent = count;
    });
    document.querySelectorAll('[data-cart-count-zero]').forEach(el => {
      el.hidden = count === '0';
    });
  }

  destroy() {
    document.removeEventListener('cart:added',   this._onCartAdded);
    document.removeEventListener('cart:updated', this._onCartUpdated);
    document.removeEventListener('cart:removed', this._onCartRemoved);
    document.removeEventListener('click',        this._onContentClick);
  }
}

document.addEventListener('DOMContentLoaded', () => {
  window.CartDrawerManager = new CartDrawerManager();
});
